import {
  Box,
  Flex,
  VStack,
} from '@chakra-ui/react';
import { useListingFilterSort } from 'hooks';
import { ListingModel } from 'models';
import ListingAccordion from '../ListingAccordion/ListingAccordion';
import ListingGridFilters from '../ListingGridFilters/ListingGridFilters';
import ListingGridSkeleton from '../ListingGridSkeleton/ListingGridSkeleton';
import EmptyState from '../../EmptyState/EmptyState';

export interface ListingGridProps {
  gridListings: ListingModel[];
  isLoading: boolean;
  showFilters?: boolean;
  showFiltersLabel?: boolean;
  showTypeFilters?: boolean;
  showAssociatedItems?: boolean;
  showAssociationAction?: boolean;
  onListingItemClick?: (id: string, name: string) => void;
  onListingArtistClick?: (id: string, name: string) => void;
  onItemSuggestionClick?: (listing: ListingModel) => void;
  onTagListingClick?: (listing: ListingModel) => void;
}

export default function ListingGrid({
  gridListings,
  isLoading,
  showFilters = true,
  showFiltersLabel = true,
  showTypeFilters = false,
  showAssociatedItems = false,
  showAssociationAction = false,
  onListingItemClick,
  onListingArtistClick,
  onItemSuggestionClick,
  onTagListingClick,
}: ListingGridProps) {
  const {
    rawQuery,
    onSearch,
    selectedYears,
    setSelectedYears,
    selectedPrices,
    setSelectedPrices,
    showOriginal,
    setShowOriginal,
    showSet,
    setShowSet,
    sortKey,
    setSortKey,
    sortType,
    setSortType,
    displayFilters,
    setDisplayFilters,
    filteredListings,
    hasFiltersApplied,
    isOnFilterMode,
    resetFilters,
  } = useListingFilterSort(gridListings);

  const data = isOnFilterMode() ? filteredListings : gridListings;

  return (
    <Flex direction="column" w="100%" gap={4}>
      <Box flex="none">
        <ListingGridFilters
          showFilters={showFilters}
          showFiltersLabel={showFiltersLabel}
          rawQuery={rawQuery}
          onSearch={onSearch}
          displayFilters={displayFilters}
          setDisplayFilters={setDisplayFilters}
          sortKey={sortKey}
          onSortBySelect={setSortKey}
          sortType={sortType}
          onSortTypeToggle={() => setSortType(t => (t === 'asc' ? 'desc' : 'asc'))}
          listings={gridListings}
          showTypeFilter={showTypeFilters}
          defaultSaleDateRange={selectedYears}
          defaultPriceRange={selectedPrices}
          defaultShowOriginal={showOriginal}
          defaultShowSet={showSet}
          showClearOption={hasFiltersApplied()}
          onListingSaleDateRangeSelect={(min, max) => setSelectedYears([min, max])}
          onListingPriceRangeSelect={(min, max) => setSelectedPrices([min, max])}
          onListingShowOriginalToggle={() => setShowOriginal(o => !o)}
          onListingShowSetToggle={() => setShowSet(s => !s)}
          onResetFilters={resetFilters}
        />
      </Box>
      <Box
        flex="1"
        maxHeight={"600px"}
        overflowY="auto"
      >
        {isLoading ? (
          <ListingGridSkeleton rows={10} />
        ) : data.length > 0 ? (
          <VStack spacing="12px" align="stretch">
            {data.map(item => (
              <ListingAccordion
                key={item.id}
                listing={item}
                onItemClick={onListingItemClick}
                onArtistClick={onListingArtistClick}
                onItemSuggestionClick={onItemSuggestionClick}
                onTagListingClick={onTagListingClick}
                showAssociatedItem={showAssociatedItems}
                showAssociationAction={showAssociationAction}
              />
            ))}
          </VStack>
        ) : (
          <EmptyState
            header="No Listings"
            description="No Results Found"
            showButton={isOnFilterMode()}
            onButtonClick={resetFilters}
            buttonText="Clear Filters"
          />
        )}
      </Box>
    </Flex>
  );
}
